import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { errMsg, inr } from "@/lib/api";
import { payWithPayU } from "@/lib/payu";
import { PostPaymentDialog } from "@/components/PostPaymentDialog";

const STEPS = ["Job Basics", "Description & Terms", "Hiring Preference"];

const HIRING_OPTIONS = [
  { id: "own", title: "My Own Recruitment Team", desc: "No additional recruitment fee. You handle sourcing and screening." },
  { id: "hr_consultant", title: "Job Spring HR Consultant", desc: "Our internal HR consultant assists with recruitment end-to-end." },
  { id: "hiring_partner", title: "Job Spring Hiring Partner", desc: "Authorized franchise/hiring partners source candidates for you." },
  { id: "both", title: "HR Consultant + Hiring Partners", desc: "Maximum reach — our consultants and partner network together." },
];

export default function PostJob() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({
    title: "", department: "", industry: "", employment_type: "full_time", vacancies: 1,
    location: "", work_mode: "office", experience_min: 0, experience_max: 0, qualification: "",
    skills: "", salary_min: "", salary_max: "", salary_type: "annual", description: "",
    responsibilities: "", required_skills: "", preferred_skills: "", benefits: "",
    working_days: "", working_hours: "", shift: "", notice_period: "", joining_timeline: "",
    domestic_international: "domestic", interview_stages: "Screening, Technical, HR",
    hiring_preference: "own", recruitment_payment_model: "prepaid",
  });

  useState(() => {
    api.get("/public/config").then(({ data }) => setConfig(data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const generateJD = async () => {
    if (!form.title) {
      toast.error("Enter a job title first");
      return;
    }
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/jd-generator", {
        title: form.title, department: form.department,
        skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
        experience_min: Number(form.experience_min), experience_max: Number(form.experience_max),
        location: form.location, work_mode: form.work_mode, employment_type: form.employment_type,
      });
      setForm((f) => ({
        ...f,
        description: data.description || f.description,
        responsibilities: data.responsibilities || f.responsibilities,
        preferred_skills: (data.preferred_skills || []).join(", "),
        benefits: (data.benefits || []).join(", "),
      }));
      toast.success("AI generated a draft JD — review and edit it");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setAiBusy(false);
    }
  };

  const save = async (submit, postingPayment = "") => {
    setBusy(true);
    try {
      const { data } = await api.post("/employer/jobs", {
        ...form,
        vacancies: Number(form.vacancies),
        experience_min: Number(form.experience_min), experience_max: Number(form.experience_max),
        salary_min: Number(form.salary_min || 0), salary_max: Number(form.salary_max || 0),
        skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
        interview_stages: form.interview_stages.split(",").map((s) => s.trim()).filter(Boolean),
        recruitment_payment_model: form.hiring_preference === "own" ? "none" : form.recruitment_payment_model,
        posting_payment: postingPayment,
        submit,
      });
      if (submit && data.needs_payment) {
        toast.success("Draft saved — complete payment to submit for review");
        const { data: pay } = await api.post("/payments/checkout", {
          kind: "job_post", ref_id: data.id, origin_url: window.location.origin,
        });
        payWithPayU(pay.payu_url, pay.params);
        return;
      }
      toast.success(submit ? "Job submitted for admin review" : "Draft saved");
      navigate("/employer/jobs");
    } catch (e) {
      const msg = errMsg(e);
      toast.error(msg);
    } finally {
      setBusy(false);
      setPayOpen(false);
    }
  };

  const rec = config?.recruitment || {};

  return (
    <div className="max-w-3xl" data-testid="post-job-page">
      <div className="flex items-center gap-2 mb-10">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-2 flex-1">
            <div className={`w-8 h-8 rounded-full grid place-items-center text-sm font-bold shrink-0 ${
              i <= step ? "bg-[#2563EB] text-white" : "bg-muted text-muted-foreground"
            }`}>
              {i + 1}
            </div>
            <span className={`text-xs font-medium hidden sm:block ${i <= step ? "text-[#0B1B3D]" : "text-muted-foreground"}`}>{s}</span>
            {i < STEPS.length - 1 && <div className={`h-0.5 flex-1 ${i < step ? "bg-[#2563EB]" : "bg-border"}`} />}
          </div>
        ))}
      </div>

      {step === 0 && (
        <div className="bg-white border border-border/60 rounded-xl p-7 space-y-5" data-testid="wizard-step-1">
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="sm:col-span-2">
              <Label>Job title *</Label>
              <Input value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-1.5" data-testid="job-title-input" />
            </div>
            <div>
              <Label>Department</Label>
              <Input value={form.department} onChange={(e) => set("department", e.target.value)} className="mt-1.5" data-testid="job-department-input" />
            </div>
            <div>
              <Label>Industry</Label>
              <Input value={form.industry} onChange={(e) => set("industry", e.target.value)} className="mt-1.5" data-testid="job-industry-input" />
            </div>
            <div>
              <Label>Employment type</Label>
              <Select value={form.employment_type} onValueChange={(v) => set("employment_type", v)}>
                <SelectTrigger className="mt-1.5" data-testid="job-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="full_time">Full time</SelectItem>
                  <SelectItem value="part_time">Part time</SelectItem>
                  <SelectItem value="contract">Contract</SelectItem>
                  <SelectItem value="internship">Internship</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Vacancies</Label>
              <Input type="number" min={1} value={form.vacancies} onChange={(e) => set("vacancies", e.target.value)} className="mt-1.5" data-testid="job-vacancies-input" />
            </div>
            <div>
              <Label>Location</Label>
              <Input value={form.location} onChange={(e) => set("location", e.target.value)} className="mt-1.5" placeholder="e.g. Bengaluru / Dubai" data-testid="job-location-input" />
            </div>
            <div>
              <Label>Work mode</Label>
              <Select value={form.work_mode} onValueChange={(v) => set("work_mode", v)}>
                <SelectTrigger className="mt-1.5" data-testid="job-workmode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="office">Office</SelectItem>
                  <SelectItem value="remote">Remote</SelectItem>
                  <SelectItem value="hybrid">Hybrid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Min experience (yrs)</Label>
              <Input type="number" min={0} value={form.experience_min} onChange={(e) => set("experience_min", e.target.value)} className="mt-1.5" data-testid="job-expmin-input" />
            </div>
            <div>
              <Label>Max experience (yrs)</Label>
              <Input type="number" min={0} value={form.experience_max} onChange={(e) => set("experience_max", e.target.value)} className="mt-1.5" data-testid="job-expmax-input" />
            </div>
            <div>
              <Label>Qualification</Label>
              <Input value={form.qualification} onChange={(e) => set("qualification", e.target.value)} className="mt-1.5" data-testid="job-qualification-input" />
            </div>
            <div>
              <Label>Skills (comma separated)</Label>
              <Input value={form.skills} onChange={(e) => set("skills", e.target.value)} className="mt-1.5" data-testid="job-skills-input" />
            </div>
            <div>
              <Label>Salary min (₹)</Label>
              <Input type="number" value={form.salary_min} onChange={(e) => set("salary_min", e.target.value)} className="mt-1.5" data-testid="job-salmin-input" />
            </div>
            <div>
              <Label>Salary max (₹)</Label>
              <Input type="number" value={form.salary_max} onChange={(e) => set("salary_max", e.target.value)} className="mt-1.5" data-testid="job-salmax-input" />
            </div>
            <div>
              <Label>Domestic / International</Label>
              <Select value={form.domestic_international} onValueChange={(v) => set("domestic_international", v)}>
                <SelectTrigger className="mt-1.5" data-testid="job-domestic-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="domestic">Domestic (India)</SelectItem>
                  <SelectItem value="international">International (UAE / global)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Interview stages (comma separated)</Label>
              <Input value={form.interview_stages} onChange={(e) => set("interview_stages", e.target.value)} className="mt-1.5" data-testid="job-stages-input" />
            </div>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="bg-white border border-border/60 rounded-xl p-7 space-y-5" data-testid="wizard-step-2">
          <div className="flex justify-end">
            <Button type="button" variant="outline" onClick={generateJD} disabled={aiBusy} className="rounded-full border-[#2563EB] text-[#2563EB]" data-testid="ai-jd-btn">
              <Sparkles className="w-4 h-4 mr-1.5" /> {aiBusy ? "Generating…" : "Generate with AI"}
            </Button>
          </div>
          <div>
            <Label>Job description</Label>
            <Textarea rows={5} value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1.5" data-testid="job-description-input" />
          </div>
          <div>
            <Label>Responsibilities</Label>
            <Textarea rows={4} value={form.responsibilities} onChange={(e) => set("responsibilities", e.target.value)} className="mt-1.5" data-testid="job-responsibilities-input" />
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label>Benefits</Label>
              <Input value={form.benefits} onChange={(e) => set("benefits", e.target.value)} className="mt-1.5" data-testid="job-benefits-input" />
            </div>
            <div>
              <Label>Notice period preference</Label>
              <Input value={form.notice_period} onChange={(e) => set("notice_period", e.target.value)} className="mt-1.5" placeholder="e.g. 30 days" data-testid="job-notice-input" />
            </div>
            <div>
              <Label>Working days</Label>
              <Input value={form.working_days} onChange={(e) => set("working_days", e.target.value)} className="mt-1.5" placeholder="Mon–Fri" />
            </div>
            <div>
              <Label>Shift</Label>
              <Input value={form.shift} onChange={(e) => set("shift", e.target.value)} className="mt-1.5" placeholder="Day" />
            </div>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-6" data-testid="wizard-step-3">
          <div className="bg-white border border-border/60 rounded-xl p-7">
            <h3 className="font-heading font-semibold text-lg text-[#0B1B3D] mb-5">How would you like to hire?</h3>
            <div className="space-y-3" role="radiogroup" aria-label="Hiring preference">
              {HIRING_OPTIONS.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => set("hiring_preference", o.id)}
                  data-testid={`hiring-${o.id}`}
                  className={`w-full text-left border rounded-xl p-5 transition-colors ${
                    form.hiring_preference === o.id
                      ? "border-[#2563EB] bg-[#2563EB]/5 ring-2 ring-[#2563EB]/25"
                      : "border-border hover:border-[#2563EB]/40"
                  }`}
                >
                  <p className="font-heading font-semibold">{o.title}</p>
                  <p className="text-sm text-muted-foreground mt-1">{o.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {form.hiring_preference !== "own" && (
            <div className="bg-white border border-border/60 rounded-xl p-7" data-testid="payment-model-section">
              <h3 className="font-heading font-semibold text-lg text-[#0B1B3D] mb-5">Select Recruitment Payment Model</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => set("recruitment_payment_model", "prepaid")}
                  data-testid="payment-model-prepaid"
                  className={`text-left border rounded-xl p-5 transition-colors ${
                    form.recruitment_payment_model === "prepaid"
                      ? "border-[#F5A623] bg-[#F5A623]/8 ring-2 ring-[#F5A623]/30"
                      : "border-border hover:border-[#F5A623]/50"
                  }`}
                >
                  <p className="font-heading font-bold">PREPAID</p>
                  <p className="font-heading font-extrabold text-2xl text-[#0B1B3D] mt-2">
                    {inr(rec.prepaid_amount || 5000)} <span className="text-xs font-normal text-muted-foreground">+ GST / successful candidate</span>
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">Deducted from your Recruitment Wallet on joining.</p>
                </button>
                <button
                  type="button"
                  onClick={() => set("recruitment_payment_model", "postpaid")}
                  data-testid="payment-model-postpaid"
                  className={`text-left border rounded-xl p-5 transition-colors ${
                    form.recruitment_payment_model === "postpaid"
                      ? "border-[#F5A623] bg-[#F5A623]/8 ring-2 ring-[#F5A623]/30"
                      : "border-border hover:border-[#F5A623]/50"
                  }`}
                >
                  <p className="font-heading font-bold">POSTPAID</p>
                  <p className="font-heading font-extrabold text-2xl text-[#0B1B3D] mt-2">
                    {rec.postpaid_pct || 8.33}% <span className="text-xs font-normal text-muted-foreground">+ GST of Annual CTC / candidate</span>
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">Invoiced on successful joining, payable in 15 days.</p>
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-5">
                {rec.replacement_terms || "Replacement terms apply according to the applicable recruitment agreement."}
              </p>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between mt-8">
        <Button variant="ghost" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="wizard-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => save(false)} disabled={busy || !form.title} className="rounded-full" data-testid="save-draft-btn">
            Save Draft
          </Button>
          {step < STEPS.length - 1 ? (
            <Button onClick={() => setStep(step + 1)} disabled={!form.title} className="rounded-full bg-[#0B1B3D]" data-testid="wizard-next-btn">
              Next <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={() => setPayOpen(true)} disabled={busy || !form.title} className="rounded-full bg-[#F5A623] text-[#0B1B3D] hover:bg-[#f7b84d] font-bold" data-testid="submit-job-btn">
              {busy ? "Submitting…" : "Submit for Review"}
            </Button>
          )}
        </div>
      </div>

      <PostPaymentDialog open={payOpen} onClose={() => setPayOpen(false)} onChoose={(mode) => save(true, mode)} busy={busy} />
    </div>
  );
}
